import type { GenericItem, Template } from "@/types/api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { templatesApi } from "./api";
import { ConfirmDialog } from "./ConfirmDialog";
import { PlaceholderDialog } from "./PlaceholderDialog";
import { templatesKeys } from "./queryKeys";
import { RenameDialog } from "./RenameDialog";

interface ConfirmState {
  title: string;
  message: string;
  onConfirm: () => void;
}

export function TemplatesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Template | null>(null);
  const [renaming, setRenaming] = useState<Template | null>(null);
  const [creating, setCreating] = useState(false);
  const [placeholder, setPlaceholder] = useState<Partial<GenericItem> | null>(null);
  const [phOpen, setPhOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const list = useQuery({ queryKey: templatesKeys.lists(), queryFn: templatesApi.list });
  const placeholders = useQuery({
    queryKey: templatesKeys.items(selected?.id),
    queryFn: () => templatesApi.listPlaceholders(selected!.id),
    enabled: !!selected,
  });

  const create = useMutation({
    mutationFn: (name: string) => templatesApi.create(name),
    onSuccess: () => {
      setCreating(false);
      void qc.invalidateQueries({ queryKey: templatesKeys.lists() });
    },
  });
  const remove = useMutation({
    mutationFn: templatesApi.remove,
    onSuccess: (_, deletedId) => {
      void qc.invalidateQueries({ queryKey: templatesKeys.lists() });
      setSelected((prev) => (deletedId === prev?.id ? null : prev));
    },
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => templatesApi.rename(id, name),
    onSuccess: () => {
      setRenaming(null);
      void qc.invalidateQueries({ queryKey: templatesKeys.lists() });
    },
  });
  const upsertPh = useMutation({
    mutationFn: (item: Partial<GenericItem>) => templatesApi.upsertPlaceholder(selected!.id, item),
    onSuccess: () => {
      setPhOpen(false);
      void qc.invalidateQueries({ queryKey: templatesKeys.items(selected?.id) });
      void qc.invalidateQueries({ queryKey: templatesKeys.lists() });
    },
  });
  const removePh = useMutation({
    mutationFn: (itemId: number) => templatesApi.removePlaceholder(selected!.id, itemId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: templatesKeys.items(selected?.id) });
      void qc.invalidateQueries({ queryKey: templatesKeys.lists() });
    },
  });

  const filtered = (list.data ?? []).filter((t) =>
    search ? t.name.toLowerCase().includes(search.toLowerCase()) : true
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper>
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Typography variant="h6" sx={{ flex: 1, minWidth: 140 }}>
            Templates
          </Typography>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: { xs: 1, sm: "0 0 auto" }, minWidth: 140 }}
          />
          <Tooltip title="New template">
            <IconButton color="primary" onClick={() => setCreating(true)}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: { xs: 480, sm: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  NUMBER OF PLACEHOLDERS
                </TableCell>
                <TableCell align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((t) => (
                <TableRow
                  key={t.id}
                  hover
                  selected={selected?.id === t.id}
                  onClick={() => setSelected(t)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{t.name}</TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    {t.items?.length ?? 0}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Rename">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenaming(t);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirm({
                            title: "Delete template",
                            message:
                              `Are you sure you want to delete "${t.name}"? This will also remove all its placeholders.`,
                            onConfirm: () => remove.mutate(t.id),
                          });
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {selected && (
        <Paper sx={{ mt: 3 }}>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ flex: 1, minWidth: 160 }}>
              Template placeholders — {selected.name}
            </Typography>
            <Tooltip title="Add placeholder">
              <IconButton
                color="primary"
                onClick={() => {
                  setPlaceholder({});
                  setPhOpen(true);
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: { xs: 520, sm: 0 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>QUANTITY</TableCell>
                  <TableCell>PLACEHOLDER NAME</TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    FURNITURECATEGORY
                  </TableCell>
                  <TableCell align="right">ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(placeholders.data ?? []).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.quantity}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      {p.categoryInfo?.name ?? ""}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setPlaceholder(p);
                          setPhOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setConfirm({
                            title: "Delete placeholder",
                            message: `Are you sure you want to delete "${p.name}"?`,
                            onConfirm: () => removePh.mutate(p.id),
                          })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <RenameDialog
        open={!!renaming}
        initial={renaming?.name ?? ""}
        onClose={() => setRenaming(null)}
        onSave={(name) => renaming && rename.mutate({ id: renaming.id, name })}
      />
      <RenameDialog
        open={creating}
        initial=""
        title="New template"
        description="What would you like to name this template?"
        saveLabel="Create"
        onClose={() => setCreating(false)}
        onSave={(name) => create.mutate(name)}
      />
      <PlaceholderDialog
        open={phOpen}
        initial={placeholder}
        onClose={() => setPhOpen(false)}
        onSave={(item) => upsertPh.mutate(item)}
      />
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title ?? ""}
        message={confirm?.message ?? ""}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm ?? (() => {})}
      />
    </Box>
  );
}
